import type { AiToolId } from "@cw/forms/ai-tools";
import { SLUG_TO_ICON } from "@/lib/modules";

/** Everything the intake collects, held as strings until the server parses it. */
export type Environment = {
  totalUsers: string;
  totalSites: string;
  currentPlatform: string;
  crm: string;
  erp: string;
  planningToMigrate: string;
  aiTools: AiToolId[];
  company: string;
  name: string;
  email: string;
  phone: string;
};

export const EMPTY_ENVIRONMENT: Environment = {
  totalUsers: "",
  totalSites: "",
  currentPlatform: "",
  crm: "",
  erp: "",
  planningToMigrate: "no",
  aiTools: [],
  company: "",
  name: "",
  email: "",
  phone: "",
};

export const MIGRATION_OPTIONS = [
  { value: "no", label: "Not planned" },
  { value: "0-6mo", label: "Within 6 months" },
  { value: "6-12mo", label: "6–12 months" },
  { value: "12mo+", label: "Later" },
  { value: "unsure", label: "Not sure" },
] as const;

export const migrationLabel = (value: string): string =>
  MIGRATION_OPTIONS.find((option) => option.value === value)?.label ?? value;

/** Autocomplete suggestions. Free text is still accepted; the schema only caps length. */
export const PLATFORM_SUGGESTIONS = [
  "Microsoft Teams", "Zoom", "RingCentral", "Cisco Webex", "Genesys Cloud", "Five9", "NICE CXone",
  "8x8", "Dialpad", "Avaya", "Mitel", "Vonage", "Talkdesk", "Amazon Connect", "GoTo Connect",
];
export const CRM_SUGGESTIONS = [
  "Salesforce", "HubSpot", "Microsoft Dynamics 365", "ServiceNow", "Zoho CRM", "Oracle CX", "Pipedrive", "SugarCRM",
];
export const ERP_SUGGESTIONS = [
  "SAP S/4HANA", "Oracle NetSuite", "Microsoft Dynamics 365", "Oracle Fusion Cloud ERP", "Workday", "Infor CloudSuite",
  "Sage Intacct", "Epicor", "Acumatica",
];

/** Licensed provider marks (see public/brand/providers/LICENSE.txt). Tools without a mark render a monogram. */
export const AI_TOOL_LOGOS: Partial<Record<AiToolId, string>> = {
  chatgpt: "/brand/providers/openai.svg",
  claude: "/brand/providers/claude.svg",
  gemini: "/brand/providers/gemini.svg",
  grok: "/brand/providers/grok.svg",
  copilot: "/brand/providers/copilot.svg",
};
export const AI_TOOL_MONOGRAMS: Partial<Record<AiToolId, string>> = {
  other: "+",
};

/** Bundle row slug → ModuleIcon name. Extends the canonical module map with the dashboard entitlement. */
export const MODULE_ICONS: Record<string, string> = {
  ...SLUG_TO_ICON,
  "executive-dashboard": "dashboard",
};

export type StepId = "bundle" | "organization" | "technology" | "contact";
export type StepStatus = "complete" | "current" | "upcoming";

export const STEPS: { id: StepId; label: string; anchor: string }[] = [
  { id: "bundle", label: "Select your Edge", anchor: "edge-step-bundle" },
  { id: "organization", label: "Organization profile", anchor: "edge-step-organization" },
  { id: "technology", label: "Technology environment", anchor: "edge-step-technology" },
  { id: "contact", label: "Contact details", anchor: "edge-step-contact" },
];

const isWholeNumber = (value: string) => /^\d+$/.test(value.trim()) && Number(value) > 0;

/** Which stages already hold enough to move on. Optional stages count once anything is entered. */
export function completedSteps(environment: Environment, hasBundle: boolean): Set<StepId> {
  const done = new Set<StepId>();
  if (hasBundle) done.add("bundle");
  if (isWholeNumber(environment.totalUsers) && isWholeNumber(environment.totalSites)) done.add("organization");
  const hasTechnology = environment.currentPlatform.trim() || environment.crm.trim() || environment.erp.trim()
    || environment.aiTools.length > 0 || environment.planningToMigrate !== EMPTY_ENVIRONMENT.planningToMigrate;
  if (hasTechnology) done.add("technology");
  if (environment.company.trim() && environment.name.trim() && /.+@.+\..+/.test(environment.email.trim())) done.add("contact");
  return done;
}

export const formatCount = (value: string): string =>
  isWholeNumber(value) ? Number(value).toLocaleString("en-US") : "";
